interface DateRange {
  value: string;
  label: string;
  forecast?: boolean;
  model?: string;
}

function getMonthDays(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function generateDateRanges(
  actualStart: { month: number; year: number },
  actualEnd: { month: number; year: number },
  forecastStart: { month: number; year: number },
  forecastEnd: { month: number; year: number },
  modelName: string = "Model 1"
): DateRange[] {
  const dateRanges: DateRange[] = [];
  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];

  // Helper function to add months
  function addMonthsToDate(
    date: { month: number; year: number },
    monthsToAdd: number
  ): { month: number; year: number } {
    const totalMonths = date.month + monthsToAdd;
    return {
      month: totalMonths % 12,
      year: date.year + Math.floor(totalMonths / 12),
    };
  }

  // Helper function to compare dates
  function isDateAfter(
    date1: { month: number; year: number },
    date2: { month: number; year: number }
  ): boolean {
    return (
      date1.year > date2.year ||
      (date1.year === date2.year && date1.month > date2.month)
    );
  }

  // Helper function to format date range
  function formatDateRange(year: number, month: number): string {
    const days = getMonthDays(year, month);
    const monthStr = (month + 1).toString().padStart(2, "0");
    return `${year}-${monthStr}-01,${year}-${monthStr}-${days}`;
  }

  // Generate actual date ranges
  let currentDate = { ...actualStart };
  while (!isDateAfter(currentDate, actualEnd)) {
    const dateRange = formatDateRange(currentDate.year, currentDate.month);
    dateRanges.push({
      value: dateRange,
      label: `${months[currentDate.month]}-${currentDate.year
        .toString()
        .slice(-2)}`,
    });
    currentDate = addMonthsToDate(currentDate, 1);
  }

  // Generate forecast date ranges
  currentDate = { ...forecastStart };
  while (!isDateAfter(currentDate, forecastEnd)) {
    const dateRange = formatDateRange(currentDate.year, currentDate.month);
    // Add both actual and forecast entries for the forecast period
    if (!dateRanges.some((dr) => dr.value === dateRange)) {
      dateRanges.push({
        value: dateRange,
        label: `${months[currentDate.month]}-${currentDate.year
          .toString()
          .slice(-2)}`,
      });
    }
    dateRanges.push({
      value: `${dateRange},${modelName}`,
      label: `${months[currentDate.month]}-${currentDate.year
        .toString()
        .slice(-2)} (Forecast)`,
      forecast: true,
      model: modelName,
    });
    currentDate = addMonthsToDate(currentDate, 1);
  }

  return dateRanges.reverse(); // Reverse to match the original format
}

// Example usage:
export const DEFAULT_DATES = generateDateRanges(
  { month: 0, year: 2021 }, // Actual start: January 2021
  { month: 10, year: 2024 }, // Actual end: November 2024
  { month: 1, year: 2025 }, // Forecast start: November 2024
  { month: 10, year: 2025 }, // Forecast end: November 2025
  "Model 1"
);

export function generatePairedDateRanges(
  actualStart: { month: number; year: number },
  actualEnd: { month: number; year: number },
  forecastStart?: { month: number; year: number },
  forecastEnd?: { month: number; year: number },
  modelName: string = "Model 1"
): DateRange[] {
  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];

  function getMonthDays(year: number, month: number): number {
    return new Date(year, month + 1, 0).getDate();
  }

  function addMonths(date: { month: number; year: number }, add: number) {
    const total = date.month + add;
    return {
      month: total % 12,
      year: date.year + Math.floor(total / 12),
    };
  }

  function isAfter(
    a: { month: number; year: number },
    b: { month: number; year: number }
  ) {
    return a.year > b.year || (a.year === b.year && a.month > b.month);
  }

  function formatRange(year: number, month: number) {
    const days = getMonthDays(year, month);
    const mm = (month + 1).toString().padStart(2, "0");
    return `${year}-${mm}-01,${year}-${mm}-${days}`;
  }

  const result: DateRange[] = [];
  let cur = { ...actualStart };

  while (!isAfter(cur, actualEnd)) {
    const val = formatRange(cur.year, cur.month);
    const label = `${months[cur.month]}-${cur.year.toString().slice(-2)}`;
    result.push({
      value: val,
      label,
    });

    // If this month is also in the forecast range, add the forecast entry right after
    if (forecastStart && forecastEnd && !isAfter(cur, forecastEnd) && !isAfter(forecastStart, cur)) {
      result.push({
        value: `${val},${modelName}`,
        label: `${label} (Forecast)`,
        forecast: true,
        model: modelName,
      });
    }

    cur = addMonths(cur, 1);
  }

  // Add any remaining forecast months after actualEnd
  if (forecastStart && forecastEnd) {
    let forecastCur = addMonths(actualEnd, 1);
    while (!isAfter(forecastCur, forecastEnd)) {
      const val = formatRange(forecastCur.year, forecastCur.month);
      const label = `${months[forecastCur.month]}-${forecastCur.year
        .toString()
        .slice(-2)}`;
      result.push({
        value: `${val},${modelName}`,
        label: `${label} (Forecast)`,
        forecast: true,
        model: modelName,
      });
      forecastCur = addMonths(forecastCur, 1);
    }
  }

  return result.reverse();
}

export const GROUP_BY = ["Total", "Discom"];
export const DEMAND_UNITS = ["MW", "MWh", "LU"];

export const DataTabs = [
  { label: "Chart", value: "chart" },
  { label: "Summary", value: "summary" },
  { label: "Table", value: "table" },
];

export const ExchangeDataTabs = [
  { label: "Chart", value: "chart" },
  { label: "Summary", value: "summary" },
  { label: "Table", value: "table" },
];

export const DataTabsT = [
  { label: "Chart", value: "chart" },
  // {label:"Summary", value: "summary"},
  { label: "Table", value: "table" },
];
